/* eslint-disable @typescript-eslint/no-explicit-any */
import { AuthenticatedHttpClient } from "@/lib/axios";
import {
  B2cSender,
  BulkB2cSenderRequest,
  BulkB2cSenderResponse,
  BulkUpdateB2cSenderRequest,
  BulkUpdateB2cSenderResponse,
  CreateB2cSenderDto,
  DeleteB2cSenderResponse,
  FilterB2cSenderDto,
  ListB2cSenderResponse,
  ManageSeedEspTypesDto,
  UpdateB2cSenderDto,
} from "@/types/b2c-sender.types";

export class B2cSenderService {
  private http: AuthenticatedHttpClient;
  private baseUrl = "/b2c-sender";

  constructor(httpClient: AuthenticatedHttpClient) {
    this.http = httpClient;
  }

  async createSender(senderData: CreateB2cSenderDto): Promise<B2cSender> {
    const response = await this.http.post<any>(this.baseUrl, senderData);
    return response as unknown as B2cSender;
  }

  async createBulkSenders(
    request: BulkB2cSenderRequest
  ): Promise<BulkB2cSenderResponse> {
    const response = await this.http.post<any>(
      `${this.baseUrl}/bulk`,
      request
    );
    return response as unknown as BulkB2cSenderResponse;
  }

  async getSenders(filters?: FilterB2cSenderDto): Promise<ListB2cSenderResponse> {
    const queryParams = new URLSearchParams();
    
    // Add pagination parameters
    if (filters?.page) queryParams.append("page", filters.page.toString());
    if (filters?.limit) queryParams.append("limit", filters.limit.toString());
    
    // Add label filters
    if (filters?.labels && filters.labels.length > 0) {
      queryParams.append("labels", filters.labels.join(","));
    }
    
    // Add subscription filters
    if (filters?.subscriptionIds && filters.subscriptionIds.length > 0) {
      queryParams.append("subscriptionIds", filters.subscriptionIds.join(","));
    }
    
    // Add plan filters
    if (filters?.planIds && filters.planIds.length > 0) {
      queryParams.append("planIds", filters.planIds.join(","));
    }
    
    // Add connection status filter
    if (filters?.connectionStatus) {
      queryParams.append("connectionStatus", filters.connectionStatus);
    }
    
    // Add sending volume filters
    if (filters?.minSendingVolume !== undefined) {
      queryParams.append("minSendingVolume", filters.minSendingVolume.toString());
    }
    if (filters?.maxSendingVolume !== undefined) {
      queryParams.append("maxSendingVolume", filters.maxSendingVolume.toString());
    }
    
    // Add seed volume filters
    if (filters?.minSeedVolume !== undefined) {
      queryParams.append("minSeedVolume", filters.minSeedVolume.toString());
    }
    if (filters?.maxSeedVolume !== undefined) {
      queryParams.append("maxSeedVolume", filters.maxSeedVolume.toString());
    }
    
    // Add seed assign filters
    if (filters?.minSeedAssign !== undefined) {
      queryParams.append("minSeedAssign", filters.minSeedAssign.toString());
    }
    if (filters?.maxSeedAssign !== undefined) {
      queryParams.append("maxSeedAssign", filters.maxSeedAssign.toString());
    }

    const url = queryParams.toString()
      ? `${this.baseUrl}?${queryParams.toString()}`
      : this.baseUrl;

    const response = await this.http.get<any>(url);
    return response as unknown as ListB2cSenderResponse;
  }

  async getSenderById(id: string): Promise<B2cSender> {
    const response = await this.http.get<any>(`${this.baseUrl}/${id}`);
    return response as unknown as B2cSender;
  }

  async updateSender(
    id: string,
    updates: UpdateB2cSenderDto
  ): Promise<B2cSender> {
    const response = await this.http.patch<any>(
      `${this.baseUrl}/${id}`,
      updates
    );
    return response as unknown as B2cSender;
  }

  async bulkUpdateSenders(
    request: BulkUpdateB2cSenderRequest
  ): Promise<BulkUpdateB2cSenderResponse> {
    const response = await this.http.patch<any>(
      `${this.baseUrl}/bulk`,
      request
    );
    return response as unknown as BulkUpdateB2cSenderResponse;
  }

  async deleteSender(id: string): Promise<DeleteB2cSenderResponse> {
    const response = await this.http.delete<any>(`${this.baseUrl}/${id}`);
    return response as unknown as DeleteB2cSenderResponse;
  }

  async exportSendersAsCsv(): Promise<Blob> {
    return this.http.get(`${this.baseUrl}/export`, {
      responseType: "blob",
    }) as unknown as Blob;
  }

  async manageSeedEspTypes(
    id: string,
    manageSeedEspTypesDto: ManageSeedEspTypesDto
  ): Promise<B2cSender> {
    const response = await this.http.patch<any>(
      `${this.baseUrl}/${id}/manage-esp-types`,
      manageSeedEspTypesDto
    );
    return response as unknown as B2cSender;
  }

  async assignPlan(
    id: string,
    assignPlanDto: { planId: string; subscriptionId: string }
  ): Promise<B2cSender> {
    const response = await this.http.post<any>(
      `${this.baseUrl}/${id}/assign-plan`,
      assignPlanDto
    );
    return response as unknown as B2cSender;
  }

  async unassignPlan(id: string): Promise<B2cSender> {
    const response = await this.http.post<any>(
      `${this.baseUrl}/${id}/unassign-plan`,
      {}
    );
    return response as unknown as B2cSender;
  }

  async createReminders(payload: { emails: string[]; remindAt: string; phoneNumber: string }): Promise<any> {
    const response = await this.http.post<any>(`${this.baseUrl}/reminders`, payload);
    return response as unknown as any;
  }

  async getReminders(): Promise<any> {
    const response = await this.http.get<any>(`${this.baseUrl}/reminders`);
    return response as unknown as any;
  }

  async deleteReminders(id: string): Promise<any> {
    const response = await this.http.delete<any>(`${this.baseUrl}/reminders/${id}`);
    return response as unknown as any;
  }

  /** GET /b2c-sender/available-labels - labels with labelType SENDER_PROFILE for assign dropdown */
  async getAvailableLabels(): Promise<{ _id: string; name: string; color: string }[]> {
    const response = await this.http.get<any>(`${this.baseUrl}/available-labels`);
    return (Array.isArray(response) ? response : response?.data ?? response ?? []) as { _id: string; name: string; color: string }[];
  }

  /** POST /b2c-sender/:id/labels - assign labels to a sender */
  async addLabels(id: string, body: { labelIds: string[] }): Promise<B2cSender> {
    const response = await this.http.post<any>(`${this.baseUrl}/${id}/labels`, body);
    return response as unknown as B2cSender;
  }

  /** DELETE /b2c-sender/:id/labels - unassign labels from a sender */
  async removeLabels(id: string, body: { labelIds: string[] }): Promise<B2cSender> {
    const response = await this.http.delete<any>(`${this.baseUrl}/${id}/labels`, { data: body });
    return response as unknown as B2cSender;
  }

  /** POST /b2c-sender/bulk-add-labels */
  async bulkAddLabels(body: { senderIds: string[]; labelIds: string[] }): Promise<{ modifiedCount?: number }> {
    const response = await this.http.post<any>(`${this.baseUrl}/bulk-add-labels`, body);
    return response as unknown as { modifiedCount?: number };
  }

  /** POST /b2c-sender/bulk-remove-labels */
  async bulkRemoveLabels(body: { senderIds: string[]; labelIds: string[] }): Promise<{ modifiedCount?: number }> {
    const response = await this.http.post<any>(`${this.baseUrl}/bulk-remove-labels`, body);
    return response as unknown as { modifiedCount?: number };
  }
}
